import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from 'components/shadcn/button';
import { Input } from 'components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import { Matcher, MatcherType } from '@/types';
import { ragApi } from '@/api/rag';

export const MatcherEditor = ({ matcher }: { matcher: Matcher }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { register, watch, handleSubmit, setValue } = useForm<Matcher>({
    defaultValues: matcher,
  });

  const matcherTypeOptions: MatcherType[] = useMemo(
    () => ['CSS_SELECTOR', 'REGEX', 'CSS_SELECTOR_AS_MARKDOWN_CLEAN'],
    [],
  );

  const updateMutation = useMutation({
    mutationFn: ragApi.updateMatcher,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rag-sources'] }),
    onError: () => toast.error(t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => ragApi.deleteMatcher(matcher.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rag-sources'] }),
    onError: () => toast.error(t('common.error')),
  });

  const onSubmit = (data: Matcher) => {
    updateMutation.mutate(data);
  };

  return (
    <div className="flex flex-col md:flex-row gap-2 items-start">
      <div className="w-full md:w-1/3">
        <Select
          value={watch('type')}
          onValueChange={(val) => {
            setValue('type', val as MatcherType);
            handleSubmit(onSubmit)();
          }}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {matcherTypeOptions.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-full md:flex-grow">
        <Input
          className="h-9"
          placeholder={t('dashboard.chatForm.rag.valueFrom')}
          {...register('valueFrom', { onBlur: handleSubmit(onSubmit) })}
        />
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="text-destructive h-9"
        onClick={() => deleteMutation.mutate()}
        disabled={deleteMutation.isPending}
      >
        ✕
      </Button>
    </div>
  );
};
